import { useState, useEffect } from 'react';
import { Database, Table, ChevronRight, ChevronDown, RefreshCw, Search, Palette, GraduationCap, Hash, Type, ToggleLeft, Calendar, Link, Columns2 as Columns, AlertCircle, Sparkles } from 'lucide-react';
import { useMappingStore } from '../../../stores/mappingStore';
import type {
  SourceModule,
  DiscoveredTable,
  DiscoveredVirtualField,
} from '../../../types/mapping';
import { SOURCE_MODULES } from '../../../types/mapping';

interface SchemaExplorerProps {
  onColumnSelect: (
    source: SourceModule,
    tableName: string,
    columnName: string,
    dataType: string,
    virtualField?: DiscoveredVirtualField | null
  ) => void;
  selectedSource: SourceModule | null;
  selectedTable: string | null;
  selectedColumn: string | null;
  selectedVirtualFieldKey?: string | null;
}

function getTypeIcon(dataType: string) {
  const dt = dataType.toLowerCase();
  if (dt.includes('int') || dt.includes('numeric') || dt.includes('float') || dt.includes('double') || dt.includes('real') || dt.includes('serial')) {
    return <Hash className="w-3 h-3" />;
  }
  if (dt.includes('bool')) {
    return <ToggleLeft className="w-3 h-3" />;
  }
  if (dt.includes('timestamp') || dt.includes('date') || dt.includes('time')) {
    return <Calendar className="w-3 h-3" />;
  }
  if (dt.includes('json')) {
    return <Columns className="w-3 h-3" />;
  }
  if (dt === 'uuid') {
    return <Link className="w-3 h-3" />;
  }
  return <Type className="w-3 h-3" />;
}

function getTypeLabel(dataType: string): string {
  const dt = dataType.toLowerCase();
  if (dt === 'character varying') return 'varchar';
  if (dt === 'double precision') return 'double';
  if (dt === 'timestamp with time zone') return 'timestamptz';
  if (dt === 'timestamp without time zone') return 'timestamp';
  if (dt === 'boolean') return 'bool';
  if (dt === 'integer') return 'int';
  if (dt === 'smallint') return 'int2';
  if (dt === 'bigint') return 'int8';
  return dt;
}

function getTypeBadgeColor(dataType: string): string {
  const dt = dataType.toLowerCase();
  if (dt.includes('int') || dt.includes('numeric') || dt.includes('float') || dt.includes('double') || dt.includes('real') || dt.includes('serial')) {
    return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
  }
  if (dt.includes('bool')) {
    return 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20';
  }
  if (dt.includes('timestamp') || dt.includes('date') || dt.includes('time')) {
    return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
  }
  if (dt.includes('json')) {
    return 'bg-teal-500/10 text-teal-400 border-teal-500/20';
  }
  if (dt === 'uuid') {
    return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
  }
  return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
}

export default function SchemaExplorer({
  onColumnSelect,
  selectedSource,
  selectedTable,
  selectedColumn,
  selectedVirtualFieldKey,
}: SchemaExplorerProps) {
  const { schema, schemaLoading, schemaError, loadSchema } = useMappingStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSources, setExpandedSources] = useState<Set<SourceModule>>(
    new Set(['ad', 'sm'])
  );
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!schema && !schemaLoading) {
      loadSchema();
    }
  }, [schema, schemaLoading, loadSchema]);

  useEffect(() => {
    if (selectedTable) {
      setExpandedTables((prev) => new Set([...prev, selectedTable]));
    }
    if (selectedSource) {
      setExpandedSources((prev) => new Set([...prev, selectedSource]));
    }
  }, [selectedTable, selectedSource]);

  const toggleSource = (source: SourceModule) => {
    setExpandedSources((prev) => {
      const next = new Set(prev);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      return next;
    });
  };

  const toggleTable = (tableName: string) => {
    setExpandedTables((prev) => {
      const next = new Set(prev);
      if (next.has(tableName)) next.delete(tableName);
      else next.add(tableName);
      return next;
    });
  };

  const filterTables = (tables: DiscoveredTable[]): DiscoveredTable[] => {
    if (!searchQuery.trim()) return tables;
    const q = searchQuery.toLowerCase();
    return tables
      .map((table) => {
        const tableMatches =
          table.table_name.toLowerCase().includes(q) ||
          (table.description || '').toLowerCase().includes(q);
        const matchingColumns = table.columns.filter(
          (col) =>
            col.column_name.toLowerCase().includes(q) ||
            col.data_type.toLowerCase().includes(q)
        );
        const matchingVirtual = (table.virtual_columns ?? []).filter(
          (vf) =>
            vf.label.toLowerCase().includes(q) ||
            vf.field_key.toLowerCase().includes(q) ||
            vf.category.toLowerCase().includes(q)
        );
        if (tableMatches) return table;
        if (matchingColumns.length > 0 || matchingVirtual.length > 0) {
          return {
            ...table,
            columns: matchingColumns,
            virtual_columns: matchingVirtual,
          };
        }
        return null;
      })
      .filter(Boolean) as DiscoveredTable[];
  };

  const getSourceIcon = (source: SourceModule) => {
    if (source === 'ad') return <Palette className="w-4 h-4 text-pink-400" />;
    return <GraduationCap className="w-4 h-4 text-blue-400" />;
  };

  const getSourceTables = (source: SourceModule): DiscoveredTable[] => {
    if (!schema) return [];
    return filterTables(schema.tables.filter((t) => t.source === source));
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-purple-400" />
          <span className="text-xs font-semibold text-white/70 uppercase tracking-wider">
            Schema Explorer
          </span>
        </div>
        <button
          onClick={() => loadSchema(true)}
          disabled={schemaLoading}
          className="p-1 rounded hover:bg-white/[0.06] text-white/40 hover:text-white/70 transition-colors disabled:opacity-50"
          title="Refresh schema"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${schemaLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="px-3 py-2 border-b border-white/[0.06]">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tables & columns..."
            className="w-full pl-8 pr-3 py-1.5 text-xs bg-white/[0.04] border border-white/[0.06] rounded-md text-white placeholder-white/30 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20 transition-colors"
          />
        </div>
      </div>

      {schemaError && (
        <div className="mx-3 mt-2 p-2 rounded-md bg-red-500/10 border border-red-500/20">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
            <p className="text-xs text-red-400">{schemaError}</p>
          </div>
        </div>
      )}

      {schemaLoading && !schema && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <RefreshCw className="w-5 h-5 text-purple-400 animate-spin mx-auto mb-2" />
            <p className="text-xs text-white/40">Discovering schema...</p>
          </div>
        </div>
      )}

      {schema && (
        <div className="flex-1 overflow-y-auto sidebar-scroll">
          {(Object.keys(SOURCE_MODULES) as SourceModule[]).map((sourceKey) => {
            const config = SOURCE_MODULES[sourceKey];
            const tables = getSourceTables(sourceKey);
            const isExpanded = expandedSources.has(sourceKey);

            return (
              <div key={sourceKey} className="border-b border-white/[0.04]">
                <button
                  onClick={() => toggleSource(sourceKey)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/[0.04] transition-colors"
                >
                  {isExpanded ? (
                    <ChevronDown className="w-3.5 h-3.5 text-white/40" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5 text-white/40" />
                  )}
                  {getSourceIcon(sourceKey)}
                  <span className={`text-xs font-semibold ${config.color}`}>
                    {config.label}
                  </span>
                  <span className="text-[10px] text-white/30 ml-auto">
                    {tables.length} table{tables.length !== 1 ? 's' : ''}
                  </span>
                </button>

                {isExpanded && (
                  <div className="pb-1">
                    {tables.length === 0 && (
                      <p className="px-8 py-2 text-xs text-white/30 italic">
                        {searchQuery ? 'No matching tables' : 'No tables discovered'}
                      </p>
                    )}
                    {tables.map((table) => {
                      const isTableExpanded = expandedTables.has(table.table_name);
                      const isSelected =
                        selectedTable === table.table_name && selectedSource === sourceKey;

                      return (
                        <div key={table.table_name}>
                          <button
                            onClick={() => toggleTable(table.table_name)}
                            className={`w-full flex items-center gap-2 pl-8 pr-3 py-1.5 hover:bg-white/[0.04] transition-colors ${
                              isSelected ? 'bg-white/[0.06]' : ''
                            }`}
                          >
                            {isTableExpanded ? (
                              <ChevronDown className="w-3 h-3 text-white/40" />
                            ) : (
                              <ChevronRight className="w-3 h-3 text-white/40" />
                            )}
                            <Table className="w-3.5 h-3.5 text-white/50" />
                            <span className="text-xs text-white/80 truncate text-left flex-1">
                              {table.table_name.replace(/^(ad|sm)_/, '')}
                            </span>
                            <span className="text-[10px] text-white/25">
                              {table.columns.length} col{table.columns.length !== 1 ? 's' : ''}
                            </span>
                          </button>

                          {isTableExpanded && (
                            <div className="pb-1">
                              {(table.virtual_columns ?? []).length > 0 && (
                                <div className="pb-1">
                                  {Object.entries(
                                    (table.virtual_columns ?? []).reduce<Record<string, DiscoveredVirtualField[]>>(
                                      (acc, vf) => {
                                        (acc[vf.category] = acc[vf.category] ?? []).push(vf);
                                        return acc;
                                      },
                                      {}
                                    )
                                  ).map(([category, fields]) => (
                                    <div key={category}>
                                      <div className="pl-14 pr-3 py-1 text-[10px] text-white/25 uppercase tracking-wider">
                                        Virtual &middot; {category}
                                      </div>
                                      {fields.map((vf) => {
                                        const isVfSelected =
                                          selectedSource === sourceKey &&
                                          selectedTable === table.table_name &&
                                          selectedVirtualFieldKey === vf.field_key;
                                        return (
                                          <button
                                            key={vf.field_key}
                                            onClick={() =>
                                              onColumnSelect(
                                                sourceKey,
                                                table.table_name,
                                                'attribute_value',
                                                vf.data_type,
                                                vf
                                              )
                                            }
                                            className={`w-full flex items-center gap-2 pl-14 pr-3 py-1 hover:bg-white/[0.06] transition-colors group ${
                                              isVfSelected ? 'bg-cyan-500/10 border-r-2 border-cyan-500' : ''
                                            }`}
                                          >
                                            <Sparkles className="w-3 h-3 text-cyan-400/70" />
                                            <span
                                              className={`text-xs truncate text-left flex-1 ${
                                                isVfSelected
                                                  ? 'text-cyan-300'
                                                  : 'text-white/60 group-hover:text-white/80'
                                              }`}
                                              title={vf.field_key}
                                            >
                                              {vf.label}
                                            </span>
                                            <span className="text-[10px] px-1.5 py-0.5 rounded border bg-cyan-500/10 text-cyan-400 border-cyan-500/20">
                                              virtual
                                            </span>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {(table.virtual_columns ?? []).length > 0 && (
                                <div className="pl-14 pr-3 py-1 text-[10px] text-white/25 uppercase tracking-wider">
                                  Columns
                                </div>
                              )}
                              {table.columns.map((col) => {
                                const isColSelected =
                                  selectedSource === sourceKey &&
                                  selectedTable === table.table_name &&
                                  selectedColumn === col.column_name &&
                                  !selectedVirtualFieldKey;

                                return (
                                  <button
                                    key={col.column_name}
                                    onClick={() =>
                                      onColumnSelect(
                                        sourceKey,
                                        table.table_name,
                                        col.column_name,
                                        col.data_type
                                      )
                                    }
                                    className={`w-full flex items-center gap-2 pl-14 pr-3 py-1 hover:bg-white/[0.06] transition-colors group ${
                                      isColSelected
                                        ? 'bg-purple-500/10 border-r-2 border-purple-500'
                                        : ''
                                    }`}
                                  >
                                    {getTypeIcon(col.data_type)}
                                    <span
                                      className={`text-xs truncate text-left flex-1 ${
                                        isColSelected
                                          ? 'text-purple-300'
                                          : 'text-white/60 group-hover:text-white/80'
                                      }`}
                                    >
                                      {col.column_name}
                                    </span>
                                    <span
                                      className={`text-[10px] px-1.5 py-0.5 rounded border ${getTypeBadgeColor(
                                        col.data_type
                                      )}`}
                                    >
                                      {getTypeLabel(col.data_type)}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {schema.discoveredAt && (
            <div className="px-3 py-2 text-[10px] text-white/20">
              Last discovered: {new Date(schema.discoveredAt).toLocaleTimeString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
